import fp from 'lodash/fp';
import React from 'react';
import { Badge, Breadcrumb, BreadcrumbItem, Button, Col, Grid, Row } from 'react-bootstrap';
import { connect } from 'react-redux';
import { LinkContainer } from 'react-router-bootstrap';
import Spinner from 'react-spinkit';
import { branch, compose, lifecycle, renderComponent, withHandlers, withProps, withStateHandlers } from 'recompose';
import { bindActionCreators } from 'redux';
import { routes } from '../../../../constants';
import { fullName } from '../../../../services';
import { businessActions, businessSelector, fullTeamPendingExternalCpdSelector, queryTeamMembersSelector } from '../../../../state/modules/business';
import { taskActions } from '../../../../state/modules/task';
import { taskCountSelector } from '../../../../state/modules/task/selectors';
import { pluralise, unfixedFloor } from '../../../../utils';
import { Icon, SortableTable, UserAvatar } from '../../../components';
import SearchForm from '../../../components/search-form';
import { ProgressChart } from '../../../partials';
import Loading from '../../loading';
import NumberBadge from '../partials/number-badge';
import { teamMemberColumns } from './team-members';

const AllTeamMembersHeader = () => (
  <section id='header-all-team-members' key='team-header' className='layout--header'>
    <Grid>
      <Row className='button-gutter'>
        <Col xs={12}>
          <Breadcrumb>
            <LinkContainer to={routes.app.teams.root}>
              <BreadcrumbItem>
                Teams
              </BreadcrumbItem>
            </LinkContainer>
            <BreadcrumbItem active>
              All CPD members
            </BreadcrumbItem>
          </Breadcrumb>
          <div className='flexbox justify-between align-baseline'>
            <div>
              <h1>
                <i className='icon-team-large' /> All CPD members
              </h1>
            </div>
          </div>
        </Col>
      </Row>
    </Grid>
  </section>
)

const AllTeamMembersDetails = branch(
  ({ queryMembersResults }) => !queryMembersResults,
  renderComponent(Loading)
)(({
  businessProgress,
  businessSize,
  columns,
  isUpdatingSearchQuery,
  loadMoreButton,
  rows,
  searchQuery,
  sortBy,
  taskCount,
  uniqueTeamPendingExternalCpd,
  updateSearchQuery
}) => (
    <section id='section--teams' className='layout--section'>
      <div className='container'>
        <div className='progress--banner'>
          <Row>
            <Col xs={12} md={3} className='banner-item'>
              <div className='chart'>
                <ProgressChart progressPercentage={businessProgress} width={120} height={120} />
              </div>
              <div className='info'>
                <p>Your organisation's overall CPD progress</p>
              </div>
            </Col>
            <Col xs={12} md={3} className='banner-item'>
              <div className='chart'>
                <NumberBadge value={businessSize} width={120} height={120} />
              </div>
              <div className='info'>
                <p>{pluralise('CPD Member', businessSize)}</p>
              </div>
            </Col>
            <Col xs={12} md={3} className='banner-item'>
              <LinkContainer className='chart clickable' to={routes.app.externalCpd.review}>
                <div>
                  <Icon className='teams-icon' icon='cpd-activity-large' />
                  <Badge bsClass={`teams-notification ${uniqueTeamPendingExternalCpd && uniqueTeamPendingExternalCpd.length > 0 ? 'teams-badge-red' : 'teams-badge-green'}`}>
                    {
                      uniqueTeamPendingExternalCpd && uniqueTeamPendingExternalCpd.length > 0 ?
                        uniqueTeamPendingExternalCpd.length < 100 ? uniqueTeamPendingExternalCpd.length : '99+'
                        : <Icon icon='accept-thick' />
                    }
                  </Badge>
                </div>
              </LinkContainer>
              <LinkContainer className='info clickable' to={routes.app.externalCpd.review}>
                <div>
                  <p>
                    {
                      uniqueTeamPendingExternalCpd && uniqueTeamPendingExternalCpd.length > 0 ?
                        `Review ${uniqueTeamPendingExternalCpd.length} CPD ${pluralise('item', uniqueTeamPendingExternalCpd.length)}` : 'No CPD for review'
                    }
                  </p>
                </div>
              </LinkContainer>
            </Col>
            <Col xs={12} md={3} className='banner-item'>
              <LinkContainer className='chart clickable' to={routes.app.tasks.home}>
                <div>
                  <Icon className='teams-icon' icon='training-large' />
                  <Badge bsClass='teams-notification teams-task teams-badge-green'>
                    {taskCount > 0 ? taskCount < 100 ? taskCount : '99+' : <Icon icon='accept-thick' />}
                  </Badge>
                </div>
              </LinkContainer>
              <LinkContainer className='info clickable' to={routes.app.tasks.home}>
                <div>
                  <p>
                    {
                      taskCount > 0 ? `See ${taskCount} active ${pluralise('task', taskCount)}` : 'No active tasks'
                    }
                  </p>
                </div>
              </LinkContainer>
            </Col>
          </Row>
        </div>
        <Row>
          <Col xs={12} md={6}>
            <h2>Team members</h2>
          </Col>
          <Col xs={12} md={6}>
            <SearchForm id='search-desktop' label='Search Members' className='searchDesktop' searchQuery={searchQuery} onSearchChange={updateSearchQuery} />
          </Col>
        </Row>
        <SortableTable
          className='table-team-members'
          rows={rows}
          columns={uniqueTeamPendingExternalCpd.length > 0 ? columns : fp.remove(({ name }) => name === 'cpdReview')(columns)}
          sortBy={sortBy}
          isFetchingAll={isUpdatingSearchQuery}
          emptyData={searchQuery && searchQuery !== '' ? `No team members match "${searchQuery}"` : 'Your business currently has no members'} />
        {loadMoreButton}
      </div>
    </section>
  ))

const AllTeamMembersPage = (props) => (
  <div className='base-layout'>
    <AllTeamMembersHeader />
    <AllTeamMembersDetails {...props} />
  </div>
);

const seeUser = (route) =>
  <LinkContainer
    className='pull-right'
    to={route}>
    <Button bsStyle='plain'><Icon icon='chevron-right-large' /></Button>
  </LinkContainer>

const defaultTake = 20;

const mapStateToProps = (state) => ({
  business: businessSelector(state),
  queryMembersResults: queryTeamMembersSelector(state),
  teamPendingExternalCpd: fullTeamPendingExternalCpdSelector(state),
  taskCount: taskCountSelector(state),
  isFetchingTeamMembers: state.business.isFetchingTeamMembers
});

const mapDispatchToProps = (dispatch) => ({
  queryTeamMembers: bindActionCreators(businessActions.business.queryTeamMembers, dispatch),
  loadMoreMembers: bindActionCreators(businessActions.business.loadMoreTeamMembers, dispatch),
  getFullTeamPendingExternalCpd: bindActionCreators(businessActions.business.getFullTeamPendingExternalCpd, dispatch),
  getTaskCount: bindActionCreators(taskActions.task.getCount, dispatch)
});

export default compose(
  connect(mapStateToProps, mapDispatchToProps),
  branch(
    ({ business }) => !business,
    renderComponent(Loading)
  ),
  lifecycle({
    componentDidMount() {
      const {
        business,
        getTaskCount,
        getFullTeamPendingExternalCpd,
        queryTeamMembers
      } = this.props;

      queryTeamMembers(
        business.id,
        {
          skip: 0,
          take: defaultTake
        }
      )

      getTaskCount();
      getFullTeamPendingExternalCpd(business.id);
    }
  }),
  branch(
    ({ queryMembersResults }) => !queryMembersResults,
    renderComponent(Loading)
  ),
  withProps(
    ({ queryMembersResults }) => ({
      teamMembers: fp.get('teamMembers')(queryMembersResults),
      businessSize: fp.get('total')(queryMembersResults),
      businessProgress: fp.get('totalProgress')(queryMembersResults)
    })
  ),
  withProps(
    ({ teamPendingExternalCpd }) => ({
      uniqueTeamPendingExternalCpd: fp.uniqBy('externalCpdId')(teamPendingExternalCpd)
    })
  ),
  withStateHandlers(() => ({
    searchQuery: '',
    searchDebounce: undefined,
    isUpdatingSearchQuery: false,
    sort: undefined
  }), {
      updateSearchDebounce: () => (searchDebounce) => ({ searchDebounce }),
      updateSearchQuery: () => (e) => ({ searchQuery: e.target.value, isUpdatingSearchQuery: true }),
      finishSearchQueryUpdate: () => () => ({ isUpdatingSearchQuery: false }),
      updateSort: () => (sort) => ({ sort })
    }),
  withProps(
    ({ queryMembersResults, searchQuery, sort }) => ({
      hasMoreToLoad: fp.get('teamMembers.length')(queryMembersResults) < fp.get('hits')(queryMembersResults),
      memberFilter: {
        skip: 0,
        take: defaultTake,
        tags: searchQuery,
        sort
      }
    })
  ),
  withHandlers({
    loadMoreMembers: ({ business, loadMoreMembers, teamMembers, memberFilter }) => () => {
      const query = {
        ...memberFilter,
        skip: teamMembers.length
      }
      loadMoreMembers(business.id, query)
    },
    refetchMembers: ({
      business,
      queryTeamMembers,
      memberFilter
    }) => () => queryTeamMembers(business.id, memberFilter),
    sortBy: ({ updateSort }) => (field, sortOrder) => {
      const sortParam = sortOrder ? `${field},${sortOrder}` : undefined;
      updateSort(sortParam)
    }
  }),
  lifecycle({
    componentWillReceiveProps({
      searchDebounce,
      updateSearchDebounce,
      refetchMembers,
      memberFilter,
      isFetchingTeamMembers,
      finishSearchQueryUpdate
    }) {
      if (!fp.isEqual(this.props.memberFilter, memberFilter)) {
        const debounced = fp.debounce(500)(refetchMembers);
        updateSearchDebounce(debounced);
        searchDebounce && searchDebounce.cancel();
        debounced();
      }
      if (this.props.isFetchingTeamMembers && !isFetchingTeamMembers) {
        finishSearchQueryUpdate();
      }
    }
  }),
  withProps(
    ({
      teamMembers,
      uniqueTeamPendingExternalCpd,
    }) => ({
      columns: teamMemberColumns,
      rows: fp.map((member) => ({
        memberName:
          <LinkContainer to={routes.app.teams.userProfiles(member.user.id)}>
            <div>
              <UserAvatar userName={fullName(member.user)} avatar={member.user.avatar} />
              <span>
                {fullName(member.user)}
                {member.isTeamManager ? <Badge bsClass='team-manager-badge'>Team manager</Badge> : null}
              </span>
            </div>
          </LinkContainer>,
        progress: `${unfixedFloor(member.progress, 0)}%`,
        cpdReview: fp.flow(
          fp.filter(({ userId }) => userId === member.user.id),
          (cpdReviews) => cpdReviews.length > 0 ?
            <LinkContainer to={routes.app.externalCpd.review}>
              <Badge bsClass='team-notification'>{cpdReviews.length > 99 ? '99+' : cpdReviews.length}</Badge>
            </LinkContainer> : null
        )(uniqueTeamPendingExternalCpd),
        seeUser: seeUser(routes.app.teams.userProfiles(member.user.id))
      }))(teamMembers)
    })
  ),
  withProps(
    ({
      hasMoreToLoad,
      isFetchingTeamMembers,
      loadMoreMembers
    }) => ({
      loadMoreButton: hasMoreToLoad ?
        <Row>
          <Col xs={4} xsOffset={4}>
            {
              isFetchingTeamMembers ?
                <Button><Spinner name='circle' className='spinner' fadeIn='none' /></Button> :
                <Button className='full' onClick={loadMoreMembers}>Load More</Button>
            }
          </Col>
        </Row> : null
    })
  )
)(AllTeamMembersPage);


// WEBPACK FOOTER //
// ./src/containers/pages/app/teams/all-members.js